import React, { useState, useEffect } from 'react';
import { Button, Modal, Typography, Space, Alert } from 'antd';
import { DownloadOutlined, WarningOutlined } from '@ant-design/icons';
import { useServiceWorkerHealth } from '../hooks/useServiceWorkerHealth';

const { Title, Text } = Typography;

const PWAInstallPrompt = () => {
  const swBroken = useServiceWorkerHealth();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // If running in browser (not standalone), show persistent banner
    setShowBanner(true);

    // Show modal every 5 minutes while on the page
    const intervalId = setInterval(
      () => {
        setShowPrompt(true);
      },
      5 * 60 * 1000,
    );

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setShowBanner(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt,
      );
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
      setIsInstalled(true);
    } else {
      console.log('User dismissed the install prompt');
    }

    // Clear the deferredPrompt
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  if (isInstalled) {
    return null;
  }

  // Defer to ServiceWorkerFixPrompt while it's showing — fix that issue first
  if (swBroken) {
    return null;
  }

  return (
    <>
      {/* Floating pill button */}
      {showBanner && (
        <div
          onClick={() => setShowPrompt(true)}
          style={{
            position: 'fixed',
            top: '25px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1001,
            backgroundColor: '#fa8c16',
            padding: '4px 12px',
            borderRadius: '18px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
            cursor: 'pointer',
          }}
        >
          <WarningOutlined style={{ color: '#fff', fontSize: '14px' }} />
          <span
            style={{
              color: '#fff',
              fontSize: '12px',
              fontWeight: 500,
              textAlign: 'center',
            }}
          >
            Neinstalirana aplikacija
          </span>
        </div>
      )}

      {/* Modal prompt */}
      {showPrompt && (
        <Modal
          visible={showPrompt}
          onCancel={() => setShowPrompt(false)}
          footer={null}
          closable={true}
          centered
          width={400}
          bodyStyle={{ padding: '24px' }}
        >
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <div style={{ textAlign: 'center' }}>
              <WarningOutlined
                style={{
                  fontSize: '48px',
                  color: '#fa8c16',
                  marginBottom: '16px',
                }}
              />
              <Title level={4} style={{ marginBottom: '8px' }}>
                Aplikacija nije instalirana!
              </Title>
              <Text type="danger">
                Koristite browser verziju - performanse i funkcionalnost su
                ograničene
              </Text>
            </div>

            <Alert
              type="warning"
              showIcon
              message="Problemi u browser verziji:"
              description={
                <ul
                  style={{
                    marginLeft: '16px',
                    marginBottom: 0,
                    fontSize: '12px',
                  }}
                >
                  <li>Offline rad ne funkcioniše</li>
                  <li>Spora sinhronizacija podataka</li>
                  <li>Problemi sa performansama</li>
                  <li>Ograničena memorija za podatke</li>
                </ul>
              }
            />

            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              {deferredPrompt && (
                <Button
                  type="primary"
                  size="large"
                  block
                  icon={<DownloadOutlined />}
                  onClick={handleInstallClick}
                  danger
                >
                  Instaliraj aplikaciju
                </Button>
              )}
              <Button block onClick={() => setShowPrompt(false)}>
                Zatvori
              </Button>
            </Space>
          </Space>
        </Modal>
      )}
    </>
  );
};

export default PWAInstallPrompt;
